import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { plansService, Plan } from '@/services/plans.service';
import {
  CreditCard, MessageCircle, Megaphone, Target, Music2, Users, Bot,
  Contact2, Send, Sparkles, Pencil, Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const LIMIT_ROWS: { key: keyof Plan['limits']; label: string; short: string; icon: React.ReactNode }[] = [
  { key: 'maxUsers',             label: 'Users / seats',      short: 'seats',      icon: <Users size={12} /> },
  { key: 'maxWhatsappAccounts',  label: 'WhatsApp accounts',  short: 'WhatsApp',   icon: <MessageCircle size={12} /> },
  { key: 'maxFacebookAccounts',  label: 'Facebook accounts',  short: 'Facebook',   icon: <Megaphone size={12} /> },
  { key: 'maxGoogleAdsAccounts', label: 'Google Ads accounts',short: 'Google Ads', icon: <Target size={12} /> },
  { key: 'maxTiktokAccounts',    label: 'TikTok accounts',    short: 'TikTok',     icon: <Music2 size={12} /> },
  { key: 'maxBotFlows',          label: 'Bot flows',          short: 'bot flows',  icon: <Bot size={12} /> },
  { key: 'maxContacts',          label: 'Contacts',           short: 'contacts',   icon: <Contact2 size={12} /> },
  { key: 'maxMessagesPerMonth',  label: 'Messages / mo',      short: 'messages',   icon: <Send size={12} /> },
  { key: 'aiCreditsPerMonth',    label: 'AI credits / mo',    short: 'AI credits', icon: <Sparkles size={12} /> },
];

const fmt = (n: number | undefined) =>
  n === undefined || n === null ? '—' : n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : String(n);

export default function PlansList() {
  const [rows, setRows] = useState<Plan[] | null>(null);
  const [del, setDel] = useState<Plan | null>(null);
  const nav = useNavigate();
  const refresh = () => plansService.list().then(setRows);
  useEffect(() => { refresh(); }, []);

  const plans = (rows ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <>
      <Topbar title="Plans" breadcrumb="Customers / Plans"
        right={<Button onClick={() => nav('/plans/new')}>+ Add Plan</Button>} />
      <div className="p-6">
        {!rows ? (
          <div className="text-sm text-text-muted">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center">
            <p className="text-sm font-medium text-text-primary">No plans yet</p>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-5 text-text-muted">
              A plan sets the default limits a company gets. Create one, then assign it when you add a client.
            </p>
            <div className="mt-3"><Button onClick={() => nav('/plans/new')}>Add your first plan</Button></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((p) => (
              <div key={p._id} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-surface transition-colors hover:border-primary/40">
                <div className="flex items-start justify-between gap-3 border-b border-border p-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                      <CreditCard size={16} />
                    </span>
                    <div>
                      <div className="text-[14px] font-semibold leading-5 text-text-primary">{p.displayName}</div>
                      <div className="font-mono text-[10px] uppercase tracking-wide text-text-muted">{p.planId}</div>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-display text-[22px] font-bold leading-7 tabular-nums text-text-primary">
                      ${p.monthlyPriceUsd}
                    </div>
                    <div className="text-[10.5px] text-text-muted">per month</div>
                  </div>
                </div>
                {(() => {
                  const set = LIMIT_ROWS.filter((f) => typeof p.limits[f.key] === 'number');
                  const unset = LIMIT_ROWS.filter((f) => typeof p.limits[f.key] !== 'number');
                  return (
                    <div className="flex-1 p-4 pt-3">
                      <dl className="space-y-0.5">
                        {set.map((f) => (
                          <div key={String(f.key)} className="flex items-center justify-between py-1">
                            <dt className="inline-flex items-center gap-1.5 text-[12px] text-text-muted">{f.icon}{f.label}</dt>
                            <dd className="text-[12.5px] font-semibold tabular-nums text-text-primary">
                              {fmt(p.limits[f.key] as number | undefined)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      {set.length === 0 && (
                        <p className="py-1 text-[12px] text-text-muted">No limits set — this plan grants nothing until you edit it.</p>
                      )}
                      {unset.length > 0 && set.length > 0 && (
                        <p className="mt-2 border-t border-border/60 pt-2 text-[11px] leading-4 text-text-muted">
                          No limit set for {unset.map((f) => f.short).join(', ')}
                        </p>
                      )}
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between border-t border-border px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 pl-1 text-[12px] text-text-muted">
                    <span className={clsx('h-1.5 w-1.5 rounded-full', p.isActive === false ? 'bg-text-muted/50' : 'bg-success')} />
                    {p.isActive === false ? 'Not assignable' : 'Assignable'}
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-text-muted hover:text-primary" onClick={() => nav(`/plans/${p._id}`)}>
                      <span className="inline-flex items-center gap-1"><Pencil size={12} /> Edit</span>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-text-muted hover:bg-danger/10 hover:text-danger" onClick={() => setDel(p)}>
                      <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Delete</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog open={!!del} title="Delete plan?" message={`Delete ${del?.displayName}? Companies on this plan keep working but lose the plan reference.`} danger
        onConfirm={async () => { await plansService.remove(del!._id); toast.success('Deleted'); setDel(null); refresh(); }}
        onCancel={() => setDel(null)} />
    </>
  );
}
